// Effective weapon parameters: WeaponDef + installed mods (WeaponModifier) + player stats.
// Pure and Phaser-free (tests/combat-mods.test.ts). Outgoing damage multipliers
// (stats.damage, crits, tag stats...) are NOT applied here: the damage system owns them.

import type { FireBehavior, StatusApplication, Tag, WeaponDef, WeaponModifier } from '../../core/types';
import { coolingRate, shotHeat } from './heat';

/** Several WeaponModifiers folded into one. */
export interface MergedMods {
  damageMult: number;
  fireRateMult: number;
  heatMult: number;
  coolMult: number;
  projectileSpeedMult: number;
  knockbackMult: number;
  spreadAdd: number;
  pierceAdd: number;
  extraProjectiles: number;
  /** Strongest burst conversion among the mods (largest count), or null. */
  burst: { count: number; interval: number } | null;
  addTags: Tag[];
  /** One entry per status id: stacks add up, duration / potency keep the strongest. */
  addStatuses: StatusApplication[];
  explodeOnKill: { radius: number; damageMult: number } | null;
}

export function emptyMods(): MergedMods {
  return {
    damageMult: 1,
    fireRateMult: 1,
    heatMult: 1,
    coolMult: 1,
    projectileSpeedMult: 1,
    knockbackMult: 1,
    spreadAdd: 0,
    pierceAdd: 0,
    extraProjectiles: 0,
    burst: null,
    addTags: [],
    addStatuses: [],
    explodeOnKill: null,
  };
}

function finite(v: number | undefined, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function mergeStatus(list: StatusApplication[], s: StatusApplication): void {
  const existing = list.find((x) => x.id === s.id);
  if (!existing) {
    list.push({ ...s });
    return;
  }
  existing.stacks = (existing.stacks ?? 1) + (s.stacks ?? 1);
  existing.duration = Math.max(existing.duration, s.duration);
  if (s.potency !== undefined) existing.potency = Math.max(existing.potency ?? 0, s.potency);
}

/** Fold mods: multiply the *Mult fields, add the *Add / extraProjectiles fields, union tags and statuses. */
export function mergeModifiers(mods: readonly (WeaponModifier | null | undefined)[]): MergedMods {
  const out = emptyMods();
  for (const m of mods) {
    if (!m) continue;
    out.damageMult *= finite(m.damageMult, 1);
    out.fireRateMult *= finite(m.fireRateMult, 1);
    out.heatMult *= finite(m.heatMult, 1);
    out.coolMult *= finite(m.coolMult, 1);
    out.projectileSpeedMult *= finite(m.projectileSpeedMult, 1);
    out.knockbackMult *= finite(m.knockbackMult, 1);
    out.spreadAdd += finite(m.spreadAdd, 0);
    out.pierceAdd += finite(m.pierceAdd, 0);
    out.extraProjectiles += finite(m.extraProjectiles, 0);
    if (m.burst && m.burst.count > 1 && (!out.burst || m.burst.count > out.burst.count)) {
      out.burst = { count: Math.round(m.burst.count), interval: Math.max(0.01, finite(m.burst.interval, 0.06)) };
    }
    for (const t of m.addTags ?? []) if (!out.addTags.includes(t)) out.addTags.push(t);
    for (const s of m.addStatuses ?? []) mergeStatus(out.addStatuses, s);
    if (m.explodeOnKill) {
      out.explodeOnKill = out.explodeOnKill
        ? {
            radius: Math.max(out.explodeOnKill.radius, m.explodeOnKill.radius),
            damageMult: Math.max(out.explodeOnKill.damageMult, m.explodeOnKill.damageMult),
          }
        : { radius: m.explodeOnKill.radius, damageMult: m.explodeOnKill.damageMult };
    }
  }
  out.extraProjectiles = Math.max(0, Math.round(out.extraProjectiles));
  out.pierceAdd = Math.round(out.pierceAdd);
  out.spreadAdd = Math.max(-Math.PI, out.spreadAdd);
  return out;
}

/** The player stats a weapon reads (damage multipliers are applied by the damage system). */
export interface WeaponStats {
  fireRate: number;
  heatCapacity: number;
  cooling: number;
}

export interface WeaponParams {
  behavior: FireBehavior;
  /** Per projectile / beam tick / full charge, including mod damageMult. */
  damage: number;
  /** Fire events (shots, bursts, beam ticks) per second. */
  fireRate: number;
  /** Seconds between fire events (never shorter than a burst). */
  interval: number;
  projectileSpeed: number;
  /** Total cone in radians (>= 0). */
  spread: number;
  pellets: number;
  range: number;
  pierce: number;
  knockback: number;
  /** Heat per fire event as a fraction of capacity (charge weapons: per second while charging). */
  heatPerShot: number;
  /** Fraction of capacity cooled per second. */
  coolPerSec: number;
  coolDelay: number;
  overheatTime: number;
  /** Seconds to full charge (charge weapons), already scaled by fire-rate modifiers. */
  chargeTime: number;
  /** Shots per fire event and their spacing: the def burst for missile_burst, a mod burst for auto/spread. */
  burst: { count: number; interval: number } | null;
  extraProjectiles: number;
  tags: Tag[];
  statuses: StatusApplication[];
  explodeOnKill: { radius: number; damageMult: number } | null;
}

export function computeWeaponParams(def: WeaponDef, mods: MergedMods, stats: WeaponStats): WeaponParams {
  const rateScale = Math.max(0.05, mods.fireRateMult) * Math.max(0.05, finite(stats.fireRate, 1));
  const fireRate = Math.max(0.05, finite(def.fireRate, 1) * rateScale);
  let burst: { count: number; interval: number } | null = null;
  if (def.behavior === 'missile_burst') {
    burst = def.burst && def.burst.count > 0 ? { count: Math.round(def.burst.count), interval: Math.max(0.01, def.burst.interval) } : { count: 3, interval: 0.08 };
  } else if ((def.behavior === 'auto' || def.behavior === 'spread') && mods.burst) {
    burst = { ...mods.burst };
  }
  let interval = 1 / fireRate;
  if (burst) interval = Math.max(interval, (burst.count - 1) * burst.interval + 0.03);
  const tags = def.tags.slice();
  for (const t of mods.addTags) if (!tags.includes(t)) tags.push(t);
  return {
    behavior: def.behavior,
    damage: Math.max(0, finite(def.damage, 0) * mods.damageMult),
    fireRate,
    interval,
    projectileSpeed: Math.max(0, finite(def.projectileSpeed, 0) * mods.projectileSpeedMult),
    spread: Math.max(0, finite(def.spread, 0) + mods.spreadAdd),
    pellets: Math.max(1, Math.round(finite(def.pellets, 1))),
    range: Math.max(16, finite(def.range, 800)),
    pierce: Math.max(0, Math.round(finite(def.pierce, 0)) + mods.pierceAdd),
    knockback: Math.max(0, finite(def.knockback, 0) * mods.knockbackMult),
    heatPerShot: shotHeat(finite(def.heatPerShot, 0), mods.heatMult, finite(stats.heatCapacity, 1)),
    coolPerSec: coolingRate(finite(def.coolRate, 0.5), mods.coolMult, finite(stats.cooling, 1)),
    coolDelay: Math.max(0, finite(def.coolDelay, 0.3)),
    overheatTime: Math.max(0.1, finite(def.overheatTime, 1.5)),
    chargeTime: Math.max(0.05, finite(def.chargeTime, 0.8) / rateScale),
    burst,
    extraProjectiles: mods.extraProjectiles,
    tags,
    statuses: mods.addStatuses.map((s) => ({ ...s })),
    explodeOnKill: mods.explodeOnKill ? { ...mods.explodeOnKill } : null,
  };
}

/**
 * Symmetric fan offsets for n projectiles: 0, +step, -step, +2·step, -2·step ...
 * Used for split-shot extras and missile fans.
 */
export function fanOffset(index: number, step: number): number {
  if (index === 0) return 0;
  const k = Math.ceil(index / 2);
  return (index % 2 === 1 ? 1 : -1) * k * step;
}
